import className from "classnames";
import { Immutable } from "immer";
import React from "react";

import { IOptionValues, IProduct } from "../../../models/catalogue";
import {
    IProductCategoryID,
    isoImageURL,
    isoProductCategoryID,
} from "../../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { defaults } from "../defaults";
import {
    IPLCOptionPanel,
    IPLCProductCategorySelector,
    IPLCProductCategorySelectorOption,
} from "../models";
import { getMostSimilarProduct } from "../product-similarity";
import { setSelectedCategory } from "../reducers";
import { ISelectedCategories } from "../reducers.interfaces";
import { selectedCategoriesSelector } from "../selectors";

// Load styles
import "./index.scss";

const BEM_NAME = "plc-configurator-category-selector-option";

/**
 * Check if selecting this option will result in a perfect product match or not. If
 * not, the option should be visually disabled (but still actually selectable).
 */
const checkIsDisabled = (
    categorySelectors: readonly IPLCProductCategorySelector[],
    optionPanels: readonly IPLCOptionPanel[],
    rootProducts: readonly IProduct[],
    selectedCategories: ISelectedCategories,
    optionValues: IOptionValues,
    selectorID: string,
    categoryID: IProductCategoryID,
): boolean => {
    const futureSelectedCategories = {
        ...selectedCategories,
        [selectorID]: categoryID,
    };
    const result = getMostSimilarProduct({
        categorySelectors: categorySelectors,
        optionPanels: optionPanels,
        rootProducts: rootProducts,
        selectedCategories: futureSelectedCategories,
        optionValues: optionValues,
        previouslySelectedCategories: selectedCategories,
        previouslySelectedOptionValues: optionValues,
    });
    return !result || result.distance > 0;
};

const handleSelectCategory = (
    selectorID: string,
    categoryID: IProductCategoryID,
    categorySelectors: readonly IPLCProductCategorySelector[],
    optionPanels: readonly IPLCOptionPanel[],
    onOptionSelected: () => void,
    dispatch: ReturnType<typeof useAppDispatch>,
) => {
    onOptionSelected();
    dispatch(
        setSelectedCategory({
            selectorID: selectorID,
            categoryID: categoryID,
            categorySelectors: categorySelectors,
            optionPanels: optionPanels,
        }),
    );
};

export const PLCProductCategorySelectorOption = (
    props: Immutable<{
        categorySelectors: readonly IPLCProductCategorySelector[];
        optionPanels: readonly IPLCOptionPanel[];
        selectorID: string;
        option: IPLCProductCategorySelectorOption;
        onOptionSelected: () => void;
    }>,
): React.ReactNode => {
    const dispatch = useAppDispatch();

    const rootProducts = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.entities.rootProducts;
    });

    const selectedCategories = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return selectedCategoriesSelector(state);
    });

    const optionValues = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.ui.optionValues;
    });

    const isChecked =
        selectedCategories[props.selectorID] === props.option.category.id;

    const groupID = props.selectorID;
    const optionID = `${props.selectorID}-${props.option.category.id}`;
    const isDisabled = checkIsDisabled(
        props.categorySelectors,
        props.optionPanels,
        rootProducts,
        selectedCategories,
        optionValues,
        props.selectorID,
        props.option.category.id,
    );

    const onSelectCategory = () => {
        handleSelectCategory(
            props.selectorID,
            props.option.category.id,
            props.categorySelectors,
            props.optionPanels,
            props.onOptionSelected,
            dispatch,
        );
    };

    const listElementClass = className({
        [`${BEM_NAME}`]: true,
        [`${BEM_NAME}--selected`]: isChecked,
        [`${BEM_NAME}--faux-disabled`]: isDisabled,
    });
    const inputClass = className({
        [`${BEM_NAME}__input`]: true,
        [`${BEM_NAME}__input--faux-disabled`]: isDisabled,
    });
    const labelClass = className({
        [`${BEM_NAME}__label`]: true,
        [`${BEM_NAME}__label--faux-disabled`]: isDisabled,
    });

    return (
        <li
            className={listElementClass}
            onClick={() => {
                if (!isChecked) {
                    onSelectCategory();
                }
            }}
        >
            {props.option.image && (
                <img
                    src={isoImageURL.unwrap(props.option.image.url)}
                    alt={props.option.image.title}
                />
            )}
            <input
                type="radio"
                className={inputClass}
                id={optionID}
                name={groupID}
                onChange={(e) => {
                    if (e.currentTarget.checked) {
                        onSelectCategory();
                    }
                }}
                value={isoProductCategoryID.unwrap(props.option.category.id)}
                checked={isChecked}
            />
            <label
                htmlFor={optionID}
                className={labelClass}
                aria-selected={false}
            >
                {props.option.category.name}
            </label>
        </li>
    );
};
